import React, { forwardRef } from 'react'

import { Box } from '../Box/Box'
import { Stack } from '../Stack/Stack'
import { FieldLabelProps, FieldLabel } from '../FieldLabel/FieldLabel'
import { FieldMessage } from '../FieldMessage/FieldMessage'

export interface TextAreaProps extends Partial<FieldLabelProps> {
  name: string
  value: string | number
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  message?: string
  tone?: 'neutral' | 'positive' | 'negative'
  disabled?: boolean
  big?: boolean
  rows?: number
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      labelWeight,
      secondary,
      tertiary,
      tertiaryIcon,
      tertiaryAction,
      description,
      message,
      tone = 'neutral',
      disabled = false,
      big,
      onChange,
      ...props
    },
    forwardedRef
  ) => {
    const borderTone = tone === 'negative' ? 'negative' : 'neutral'

    return (
      <Stack space="xs">
        {label && (
          <FieldLabel
            label={label}
            labelWeight={labelWeight}
            secondary={secondary}
            tertiary={tertiary}
            tertiaryIcon={tertiaryIcon}
            tertiaryAction={tertiaryAction}
            description={description}
          />
        )}

        <Box
          ref={forwardedRef}
          display="flex"
          component="textarea"
          alignItems="center"
          justifyContent="between"
          width="100%"
          height="var(--space--xxl)"
          border={`1px solid var(--textfield-border--${borderTone})`}
          radius="xxs"
          insetSquish={big ? 'm' : 's'}
          background={disabled ? 'var(--textfield-bg--disabled)' : 'none'}
          onChange={onChange}
          disabled={disabled}
          style={{
            outline: 'none',
            fontSize: big ? '1.6rem' : undefined
          }}
          {...props}
        />

        {message && (
          <FieldMessage tone={tone === 'neutral' ? 'light' : tone}>{message}</FieldMessage>
        )}
      </Stack>
    )
  }
)
